import { css, nothing } from 'lit'
import { html, unsafeStatic } from 'lit/static-html.js'
import HHQuizElement from '../element.js'
import { repeat } from 'lit/directives/repeat.js'

/**
 * Product Card
 * @extends HHQuizElement
 */

class HHQuizProductCard extends HHQuizElement {

  static properties = {
    variant: { type: Object },
    size: { type: String },
    quantity: { type: Number },
    id: { type: String },
    showDescription: { type: Boolean },
    max: { type: Number }
  }

  constructor() {
    super()
    this.size = 'sm'
  }

  /**
   * Renders product card
   * @returns {TemplateResult}
   */
  render() {
    return html`
    <div class="hh-quiz-product-card ${this.size}">
          <a @keydown="${(ev) => ev.stopPropagation()}" href="${this.variant.url}" class="hh-quiz-product-card__image">
            <div class="hh-quiz-product-card__image__wrapper">
              ${this.variant.image ? html`<img src="${this.variant.image.src}" alt="${this.variant.image.alt}" />` : ''}
            </div>
          </a>
          <div class="hh-quiz-product-card__content">
          <div class="hh-quiz-product-card__info">
            <div class="hh-quiz-product-card__info__text">
              <a @keydown="${(ev) => ev.stopPropagation()}" href="${this.variant.url}" class="hh-quiz-product-card__title">
                <h3 class="hh-quiz-product-card__product-title ${this.size == 'lg' ? 'h2' : 'utility'}">${this.variant.product.title}</h3>
                ${!this.variant.product.hasOnlyDefaultVariant ? html`<div class="hh-quiz-product-card__variant-title ${this.size == 'lg' ? 'utility' : 'caption'}">${this.variant.title}</div>` : ''}
              </a>
              <hh-quiz-product-card-description class="only-desktop" .variant="${this.variant}" .showDescription="${this.showDescription}"></hh-quiz-product-card-description>

              ${this.size == 'lg' & this.variant.notes.length > 0 ? html`
                <div class="hh-quiz-product-card__product-notes">
                  <h4 class="hh-quiz-product-card__product-notes__title">${HH_QUIZ.t.cart.usage_notes}</h4>
                  ${repeat(
      this.variant.notes,
      (note) => note,
      (note) => html`<div class="hh-quiz-product-card__product-notes__item caption">${note}</div>`
    )
        }
                </div>` : nothing}
              </div>
            <a @keydown="${(ev) => ev.stopPropagation()}" href="${this.variant.url}" class="hh-quiz-product-card__info__price ${this.size == 'lg' ? 'h2' : 'utility'}">
              ${this.variant.priceFormatted}${this.variant.compareAtPrice ? html` <span class="compare-at">${this.variant.compareAtPriceFormatted}</span>` : ''}
            </a>
          </div>
          <div class="hh-quiz-product-card__actions">
          <slot name="actions">
            <hh-quantity-input
            @keydown="${(ev) => ev.stopPropagation()}"
            classes="body"
            @changeQuantity="${(ev) =>
        this.store.setOrderLineQuantity(
          this.id,
          parseInt(ev.detail.value)
        )}"
            .initial="${this.quantity}"
            key="${this.id}"
            min=${0}
            max=${this.max ? Math.max(this.max, 1) : undefined}
          >
            <button name="minus" type="button" slot="decrease">
              <span class="visually-hidden">Decrease</span>
              ${unsafeStatic(HH_QUIZ['icons'].minus)}
            </button>
            <button name="plus" type="button" slot="increase">
              <span class="visually-hidden">Increase</span>
              ${unsafeStatic(HH_QUIZ['icons'].plus)}
            </button>
          </hh-quantity-input>
            <hh-button
              theme="tertiary"
              @keydown="${(ev) => ev.stopPropagation()}"
              @click="${() => this.store.setOrderLineQuantity(this.id, 0)}"
            >
              ${HH_QUIZ.t.cart.remove}
            </hh-button>
          </div>
          </slot>
        </div>
      </div>
      ${this.showDescription ? html`
      <div class="hh-quiz-product-card__mobile-footer only-mobile">
        <hh-quiz-product-card-description .variant="${this.variant}" .showDescription="${this.showDescription}"></hh-quiz-product-card-description>
        <slot name="mobile-footer"></slot>
      </div>
      `: nothing}
      <slot name="footer"></slot>
    `
  }

  static styles = [
    HHQuizElement.styles,
    css`
      .hh-quiz-product-card {
        padding: 0 0;
        display: flex;
        align-items: stretch;
      }

      .hh-quiz-product-card__image {
        flex: 1;
      }

      .lg .hh-quiz-product-card__image {
        max-width: max(10vw, 151px);
        margin-right: 60px;
        width: 100%;
      }

      .sm .hh-quiz-product-card__image {
        max-width: max(90px, 6vw);
        margin-right: 20px;
        width: 100%;
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .lg .hh-quiz-product-card__image {
          max-width: max(90px, 6vw);
          margin-right: 20px;
          width: 100%;
        }
      }

      .hh-quiz-product-card__image__wrapper {
        width: 100%;
        padding-bottom: 133.33%;
        position: relative;
        background-color: var(--hh-color-grey-mineral);
      }

      .hh-quiz-product-card__image img {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .hh-quiz-product-card__content {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .hh-quiz-product-card__actions {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin-top: 10px;
      }

      button[name="minus"] svg,
      button[name="plus"] svg {
        width: 10px;
        height: auto;
      }

      .hh-quiz-product-card * {
        color: var(--hh-color-aged-black);
      }

      .hh-quiz-product-card__title {
        display: block;
        text-decoration: none;
      }

      .lg .hh-quiz-product-card__title {
        margin-bottom: 30px;
      }

      .sm .hh-quiz-product-card__title {
        margin-bottom: 12px;
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .lg .hh-quiz-product-card__title {
          margin-bottom: 12px;
        }
      }

      .hh-quiz-product-card__product-title {
        text-decoration: none;
      }

      .hh-quiz-product-card__product-notes {
        margin-top: 20px;
        padding: 0;
        border: 0;
      }

      .hh-quiz-product-card__product-notes__title,
      .hh-quiz-product-card__product-notes__item {
        margin-bottom: 10px;
      }

      .hh-quiz-product-card__product-notes__item:last-child {
        margin-bottom: 0;
      }

      .hh-quiz-product-card__variant-title {
        color: var(--hh-color-ancient-ore);
      }

      .hh-quiz-product-card__info {
        display: flex;
        justify-content: space-between;
        flex-grow: 1;
        text-decoration: none;
      }

      .lg .hh-quiz-product-card__info {
        margin-bottom: 30px;
      }

      .sm .hh-quiz-product-card__info {
        margin-bottom: 12px;
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .lg .hh-quiz-product-card__info {
          margin-bottom: 12px;
        }
      }

      .hh-quiz-product-card__info__text {
        flex: 1;
        padding-right: 15px;
      }

      .lg .hh-quiz-product-card__info__text {
        max-width: 513px;
      }

      .sm .hh-quiz-product-card__info__text {
        max-width: 218px;
      }

      .hh-quiz-product-card__info__price {
        display: block;
        text-decoration: none;
      }

      .hh-quiz-product-card__info__price .compare-at {
        color: var(--hh-color-unrefined-silver);
        display: inline-block;
        position: relative;
      }

      .hh-quiz-product-card__info__price .compare-at::after {
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: var(--hh-color-unrefined-silver);
        margin-top: -1px;
      }

      hh-button[theme="tertiary"] {
        --hh-spacing-1: 0;
        transform: translateY(-3px);
      }

      .hh-quiz-product-card__mobile-footer {
        margin-top: 20px;
        text-decoration: none;
      }
    `,
  ]
}

customElements.define(
  'hh-quiz-product-card',
  HHQuizProductCard
)
